import type { JSX } from 'react';
import {
  Controller,
  type Control,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
  type UseFormSetValue,
} from 'react-hook-form';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { CustomField } from '@/types/machine-types';

/**
 * Plan 12-03 — KpiValuesInput
 *
 * Renders one input per `CustomField` from a MachineType schema, identical
 * structure to Phase 11's CustomFieldValuesForm but with `kpi-row-{key}*`
 * data-testids so Plan 12-04 E2E selectors can disambiguate machine custom
 * fields from logsheet KPI values.
 *
 * Layout per CustomField.dataType:
 *  - text     → shadcn `<Input type="text">`
 *  - number   → shadcn `<Input type="number">` (coerces value → number on change)
 *  - date     → shadcn `<Input type="date">` (HTML5 date picker; ISO YYYY-MM-DD)
 *  - dropdown → shadcn `<Select>` populated from `field.dropdownOptions`
 *
 * Honours D-110: required asterisk on label; per-field Zod errors surface
 * inline via `kpi-row-{key}-error`. Empty schema renders a `kpi-empty` alert.
 */

export interface KpiValuesInputProps<TFormValues extends FieldValues> {
  readonly schema: readonly CustomField[];
  readonly control: Control<TFormValues>;
  readonly setValue: UseFormSetValue<TFormValues>;
  readonly errors?: FieldErrors<TFormValues>;
  readonly disabled?: boolean;
  /** Override the RHF field-name prefix; defaults to `'kpiValues'`. */
  readonly fieldsName?: string;
}

interface FieldErrorWithMessage {
  readonly message?: string;
}

function getFieldError(
  errors: FieldErrors | undefined,
  fieldsName: string,
  key: string,
): FieldErrorWithMessage | undefined {
  if (errors === undefined) return undefined;
  const slice = errors[fieldsName];
  if (slice === undefined || typeof slice !== 'object') return undefined;
  const candidate = (slice as Record<string, unknown>)[key];
  if (candidate === undefined || typeof candidate !== 'object') return undefined;
  const message = (candidate as { message?: unknown }).message;
  if (typeof message === 'string') {
    return { message };
  }
  return undefined;
}

function toInputString(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return String(value);
  return '';
}

export function KpiValuesInput<TFormValues extends FieldValues>({
  schema,
  control,
  setValue: _setValue,
  errors,
  disabled = false,
  fieldsName = 'kpiValues',
}: KpiValuesInputProps<TFormValues>): JSX.Element {
  if (schema.length === 0) {
    return (
      <Alert data-testid="kpi-empty">
        <AlertTitle>No KPI fields</AlertTitle>
        <AlertDescription>This machine type has no KPI fields defined.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4" data-testid="kpi-root">
      {schema.map((field) => {
        const path = `${fieldsName}.${field.key}` as FieldPath<TFormValues>;
        const fieldError = getFieldError(errors, fieldsName, field.key);
        const labelText = field.required ? `${field.label} *` : field.label;

        return (
          <div key={field.key} className="space-y-1" data-testid={`kpi-row-${field.key}`}>
            <Label htmlFor={`kpi-${field.key}`}>{labelText}</Label>

            {field.dataType === 'dropdown' && field.dropdownOptions !== null && (
              <Controller
                control={control}
                name={path}
                render={({ field: rhf }) => (
                  <Select
                    value={toInputString(rhf.value)}
                    onValueChange={(v) => {
                      rhf.onChange(v);
                    }}
                    disabled={disabled}
                  >
                    <SelectTrigger
                      id={`kpi-${field.key}`}
                      data-testid={`kpi-row-${field.key}-input`}
                      aria-invalid={fieldError !== undefined}
                    >
                      <SelectValue placeholder="Select an option…" />
                    </SelectTrigger>
                    <SelectContent>
                      {(field.dropdownOptions ?? []).map((opt, idx) => (
                        <SelectItem
                          key={opt}
                          value={opt}
                          data-testid={`kpi-row-${field.key}-option-${String(idx)}`}
                        >
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            )}

            {field.dataType === 'number' && (
              <Controller
                control={control}
                name={path}
                render={({ field: rhf }) => (
                  <Input
                    id={`kpi-${field.key}`}
                    type="number"
                    autoComplete="off"
                    disabled={disabled}
                    aria-invalid={fieldError !== undefined}
                    value={toInputString(rhf.value)}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') {
                        rhf.onChange(null);
                        return;
                      }
                      const n = Number(raw);
                      rhf.onChange(Number.isFinite(n) ? n : raw);
                    }}
                    onBlur={rhf.onBlur}
                    ref={rhf.ref}
                    data-testid={`kpi-row-${field.key}-input`}
                  />
                )}
              />
            )}

            {field.dataType === 'date' && (
              <Controller
                control={control}
                name={path}
                render={({ field: rhf }) => (
                  <Input
                    id={`kpi-${field.key}`}
                    type="date"
                    autoComplete="off"
                    disabled={disabled}
                    aria-invalid={fieldError !== undefined}
                    value={toInputString(rhf.value)}
                    onChange={(e) => {
                      rhf.onChange(e.target.value === '' ? null : e.target.value);
                    }}
                    onBlur={rhf.onBlur}
                    ref={rhf.ref}
                    data-testid={`kpi-row-${field.key}-input`}
                  />
                )}
              />
            )}

            {field.dataType === 'text' && (
              <Controller
                control={control}
                name={path}
                render={({ field: rhf }) => (
                  <Input
                    id={`kpi-${field.key}`}
                    type="text"
                    autoComplete="off"
                    disabled={disabled}
                    aria-invalid={fieldError !== undefined}
                    value={toInputString(rhf.value)}
                    onChange={(e) => {
                      rhf.onChange(e.target.value);
                    }}
                    onBlur={rhf.onBlur}
                    ref={rhf.ref}
                    data-testid={`kpi-row-${field.key}-input`}
                  />
                )}
              />
            )}

            {fieldError?.message !== undefined && (
              <p className="text-xs text-destructive" data-testid={`kpi-row-${field.key}-error`}>
                {fieldError.message}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
